import { useEffect, useMemo, useState } from "react";
import { classNames } from "helpers";
import useQueryParam from "services/hooks/useQueryParam";
import { Tab, TabProps } from "./types";

const variantStyles = {
  underline: {
    container: "flex border-b border-slate-200 shrink-0",
    button: (active: boolean) =>
      classNames(
        "px-4 pt-3 pb-2 text-md font-medium border-b-2 transition-colors duration-200 focus:outline-none relative",
        active
          ? "border-sky-600 text-sky-600"
          : "border-transparent text-gray-500 hover:text-sky-600"
      ),
    errorDot: "absolute top-2 right-1 h-2 w-2 bg-red-500 rounded-full",
  },
  pill: {
    container: "self-start inline-flex items-center gap-1 rounded-md bg-slate-100 p-1.5 shrink-0",
    button: (active: boolean) =>
      classNames(
        "px-4 py-1.5 text-sm font-medium rounded-sm transition-colors duration-200 focus:outline-none relative",
        active
          ? "bg-white text-sky-600 shadow-sm"
          : "text-gray-500 hover:text-sky-600"
      ),
    errorDot: "absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full",
  },
} as const;

const getInitialIndexFromHash = (tabs: Tab[]): number => {
  const hash = window.location.hash;
  if (!hash) return 0;
  const index = tabs.findIndex((tab) => tab.hash === hash);
  return index >= 0 ? index : 0;
};

const Tabs: React.FC<TabProps> = (props) => {
  const { tabs, className = "", activeIndex: controlledActiveIndex, onTabChange, queryParamName = "tab", variant = "underline" } = props;
  const styles = variantStyles[variant];

  const hasHashes = tabs.some((tab) => tab.hash);
  const hasQueryParams = tabs.some((tab) => tab.queryParam);

  // Query param state — `useQueryParam` brine o URL init-u, replaceState-u
  // i popstate-u. Hash path ostaje inline (legacy). Hook se uvijek poziva da
  // ne kršimo Rules of Hooks; ako tabovi nemaju queryParam, vrijednost se
  // jednostavno ignorira u derivaciji activeIndex-a ispod.
  const defaultQueryValue = tabs.find((t) => t.queryParam)?.queryParam ?? "";
  const [queryValue, setQueryValue] = useQueryParam(queryParamName, defaultQueryValue);

  const initialIndexFromQuery = useMemo(() => {
    if (!hasQueryParams) return 0;
    const index = tabs.findIndex((tab) => tab.queryParam === queryValue);
    return index >= 0 ? index : 0;
  }, [tabs, hasQueryParams, queryValue]);

  const [internalActiveIndex, setInternalActiveIndex] = useState(() => {
    if (hasQueryParams) return initialIndexFromQuery;
    if (hasHashes) return getInitialIndexFromHash(tabs);
    return 0;
  });
  const [visitedTabs, setVisitedTabs] = useState<Set<number>>(
    () => new Set([
      hasQueryParams
        ? initialIndexFromQuery
        : hasHashes
          ? getInitialIndexFromHash(tabs)
          : 0,
    ])
  );

  // Use controlled state if provided, otherwise use internal state. Popis tabova zna se
  // smanjiti dok je neki aktivan (npr. uvjetni tab nestane), pa se indeks drži unutar
  // granica — inače se ne prikaže nijedan sadržaj.
  const requestedIndex =
    controlledActiveIndex !== undefined ? controlledActiveIndex : internalActiveIndex;
  const activeIndex = Math.min(Math.max(requestedIndex, 0), Math.max(tabs.length - 1, 0));

  // Mark tab as visited when it becomes active
  useEffect(() => {
    setVisitedTabs(prev => {
      if (prev.has(activeIndex)) return prev;
      const next = new Set(prev);
      next.add(activeIndex);
      return next;
    });
  }, [activeIndex]);

  // Drži internalActiveIndex u sync s queryValue — pokriva popstate /
  // browser back/forward (jer hook ažurira queryValue, a mi reagiramo
  // re-derivacijom indeksa).
  useEffect(() => {
    if (!hasQueryParams) return;
    if (initialIndexFromQuery !== internalActiveIndex) {
      if (onTabChange) onTabChange(initialIndexFromQuery);
      else setInternalActiveIndex(initialIndexFromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIndexFromQuery, hasQueryParams]);

  // Sync hash to URL when active tab changes (legacy support)
  useEffect(() => {
    if (!hasHashes || hasQueryParams) return;
    const tab = tabs[activeIndex];
    if (tab?.hash && window.location.hash !== tab.hash) {
      window.history.replaceState(null, "", `${window.location.pathname}${tab.hash}`);
    }
  }, [activeIndex, tabs, hasHashes, hasQueryParams]);

  // Listen for hash changes (legacy support — query path popstate handla useQueryParam)
  useEffect(() => {
    if (!hasHashes || hasQueryParams) return;
    const onHashChange = () => {
      const index = tabs.findIndex((tab) => tab.hash === window.location.hash);
      if (index >= 0 && index !== activeIndex) {
        if (onTabChange) {
          onTabChange(index);
        } else {
          setInternalActiveIndex(index);
        }
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [tabs, activeIndex, hasHashes, hasQueryParams, onTabChange]);

  const handleTabClick = (index: number) => {
    if (hasQueryParams) {
      const target = tabs[index]?.queryParam;
      if (target) setQueryValue(target);
    }
    if (onTabChange) {
      onTabChange(index);
    } else {
      setInternalActiveIndex(index);
    }
  };

  return (
    <div className={`w-full h-full flex flex-col ${className}`}>
      {/* Tab Headers */}
      <div className={styles.container}>
        {tabs.map((tab: Tab, index: number) => (
          <button
            key={index}
            onClick={() => handleTabClick(index)}
            className={styles.button(activeIndex === index)}
            type="button"
          >
            {tab.label}
            {tab.hasError && <span className={styles.errorDot}></span>}
          </button>
        ))}
      </div>

      {/* Tab Content - lazy loaded on first visit */}
      {tabs.map((tab: Tab, index: number) => {
        if (!visitedTabs.has(index)) return null;
        return (
          <div key={index} className={`flex-1 overflow-visible ${activeIndex === index ? "" : "hidden"}`}>
            {tab.content}
          </div>
        );
      })}
    </div>
  );
};

export default Tabs;
